import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Heart, Sparkles, Baby, Bone, Activity, Brain, Eye, Ear, Smile, Pill, Droplet, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { specializations } from '../mockData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const iconMap = {
  Stethoscope,
  Heart,
  Sparkles,
  Baby,
  Bone,
  Activity,
  Brain,
  Eye,
  Ear,
  Smile,
  Pill,
  Droplet
};

const AllSpecializations = () => {
  const navigate = useNavigate();

  // Add metadata to specializations (no pricing)
  const specializationsWithMetadata = specializations.map(spec => ({
    ...spec,
    responseTime: '5 minutes',
    availability: '24/7 Available'
  }));

  // Custom SVG illustrations for each specialty
  const getSpecialtySVG = (name) => {
    const svgs = {
      'General Medicine': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#E8F4FF"/>
          <circle cx="100" cy="80" r="35" fill="#FFD28A"/>
          <path d="M65 115 Q65 100 100 100 Q135 100 135 115 L135 160 Q135 170 125 170 L75 170 Q65 170 65 160 Z" fill="white"/>
          <path d="M70 120 L70 145 M130 120 L130 145" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="85" cy="150" r="6" fill="#286CFF"/>
          <circle cx="115" cy="150" r="6" fill="#286CFF"/>
          <path d="M85 150 Q100 155 115 150" stroke="#286CFF" strokeWidth="2" fill="none"/>
        </svg>
      ),
      'Cardiology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#FFE8E8"/>
          <path d="M100 140 L70 110 Q60 100 60 85 Q60 70 70 65 Q80 60 90 70 L100 80 L110 70 Q120 60 130 65 Q140 70 140 85 Q140 100 130 110 Z" fill="#FF6B6B"/>
          <path d="M85 95 L95 105 L115 85" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <circle cx="75" cy="85" r="3" fill="white"/>
          <circle cx="125" cy="85" r="3" fill="white"/>
        </svg>
      ),
      'Dermatology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#FFF8E8"/>
          <circle cx="100" cy="100" r="60" fill="#FFD28A"/>
          <circle cx="80" cy="90" r="8" fill="white" opacity="0.8"/>
          <circle cx="120" cy="95" r="6" fill="white" opacity="0.8"/>
          <circle cx="100" cy="130" r="10" fill="white" opacity="0.8"/>
          <path d="M85 110 Q100 115 115 110" stroke="#4A4A4A" strokeWidth="2" fill="none"/>
        </svg>
      ),
      'Pediatrics': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#E8F8FF"/>
          <circle cx="100" cy="75" r="25" fill="#FFD28A"/>
          <circle cx="90" cy="70" r="3" fill="#4A4A4A"/>
          <circle cx="110" cy="70" r="3" fill="#4A4A4A"/>
          <path d="M95 80 Q100 83 105 80" stroke="#4A4A4A" strokeWidth="2" fill="none"/>
          <rect x="75" y="100" width="50" height="60" rx="5" fill="#79A8FF"/>
          <circle cx="100" cy="130" r="8" fill="white"/>
          <path d="M100 122 L100 138 M92 130 L108 130" stroke="#79A8FF" strokeWidth="3"/>
        </svg>
      ),
      'Orthopedics': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#F0F8FF"/>
          <rect x="85" y="50" width="30" height="100" rx="15" fill="#286CFF" opacity="0.3"/>
          <rect x="90" y="60" width="20" height="80" rx="10" fill="white"/>
          <line x1="100" y1="70" x2="100" y2="130" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="100" cy="75" r="5" fill="#286CFF"/>
          <circle cx="100" cy="95" r="5" fill="#286CFF"/>
          <circle cx="100" cy="115" r="5" fill="#286CFF"/>
        </svg>
      ),
      'Gynecology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#FFE8F8"/>
          <circle cx="100" cy="90" r="40" fill="#FF6B9D" opacity="0.3"/>
          <path d="M100 130 L100 160 M85 145 L115 145" stroke="#FF6B9D" strokeWidth="5"/>
          <circle cx="100" cy="90" r="25" fill="white"/>
          <path d="M85 90 Q85 75 100 75 Q115 75 115 90" fill="#FF6B9D"/>
        </svg>
      ),
      'Neurology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#F5E8FF"/>
          <circle cx="100" cy="100" r="50" fill="#9B7EFF" opacity="0.3"/>
          <path d="M70 90 Q80 70 100 70 Q120 70 130 90" stroke="#9B7EFF" strokeWidth="3" fill="none"/>
          <circle cx="85" cy="95" r="8" fill="#9B7EFF"/>
          <circle cx="100" cy="105" r="8" fill="#9B7EFF"/>
          <circle cx="115" cy="95" r="8" fill="#9B7EFF"/>
          <path d="M85 95 L100 105 L115 95" stroke="#9B7EFF" strokeWidth="2"/>
        </svg>
      ),
      'Ophthalmology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#E8F5FF"/>
          <ellipse cx="100" cy="100" rx="50" ry="30" fill="white" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="100" cy="100" r="20" fill="#286CFF"/>
          <circle cx="100" cy="100" r="12" fill="#4A4A4A"/>
          <circle cx="105" cy="95" r="5" fill="white"/>
        </svg>
      ),
      'ENT': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#FFF5E8"/>
          <ellipse cx="70" cy="100" rx="15" ry="25" fill="#FFD28A"/>
          <path d="M85 100 Q90 95 95 100 Q90 105 85 100" fill="#FF6B6B"/>
          <circle cx="130" cy="95" r="20" fill="white" stroke="#286CFF" strokeWidth="3"/>
          <path d="M120 110 Q130 115 140 110" stroke="#286CFF" strokeWidth="2" fill="none"/>
        </svg>
      ),
      'Psychiatry': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#E8FFF5"/>
          <circle cx="100" cy="100" r="45" fill="#28C08A" opacity="0.2"/>
          <path d="M70 95 Q70 70 100 70 Q130 70 130 95" stroke="#28C08A" strokeWidth="3" fill="none"/>
          <circle cx="85" cy="95" r="5" fill="#28C08A"/>
          <circle cx="115" cy="95" r="5" fill="#28C08A"/>
          <path d="M80 115 Q100 125 120 115" stroke="#28C08A" strokeWidth="3" fill="none"/>
        </svg>
      ),
      'Gastroenterology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#FFF0E8"/>
          <ellipse cx="100" cy="80" rx="30" ry="35" fill="#FFD28A" opacity="0.5"/>
          <path d="M85 115 Q75 125 75 140 Q75 155 85 165" stroke="#FF8C42" strokeWidth="4" fill="none"/>
          <path d="M100 115 Q95 130 95 145 Q95 160 100 170" stroke="#FF8C42" strokeWidth="4" fill="none"/>
          <path d="M115 115 Q125 125 125 140 Q125 155 115 165" stroke="#FF8C42" strokeWidth="4" fill="none"/>
        </svg>
      ),
      'Urology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <circle cx="100" cy="100" r="90" fill="#E8F0FF"/>
          <ellipse cx="100" cy="90" rx="35" ry="40" fill="#79A8FF" opacity="0.3"/>
          <path d="M100 130 L100 160" stroke="#286CFF" strokeWidth="5"/>
          <circle cx="75" cy="100" r="15" fill="#79A8FF" opacity="0.5"/>
          <circle cx="125" cy="100" r="15" fill="#79A8FF" opacity="0.5"/>
        </svg>
      )
    };
    return svgs[name] || svgs['General Medicine'];
  };

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Banner Section */}
      <section style={styles.banner}>
        <div style={styles.container}>
          <div style={styles.bannerContent}>
            <h1 style={styles.bannerTitle}>Consult Top Specialists Online</h1>
            <p style={styles.bannerSubtitle}>
              Get instant medical consultation from verified doctors across 12+ specialties
            </p>
            <div style={styles.features}>
              <div style={styles.feature}>
                <CheckCircle size={20} style={{ color: '#FFD28A' }} />
                <span>Verified Doctors</span>
              </div>
              <div style={styles.feature}>
                <CheckCircle size={20} style={{ color: '#FFD28A' }} />
                <span>Digital Prescription</span>
              </div>
              <div style={styles.feature}>
                <CheckCircle size={20} style={{ color: '#FFD28A' }} />
                <span>Free Follow-up</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Specializations Grid */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Choose Your Specialty</h2>
            <p style={styles.sectionSubtitle}>
              Connect with expert doctors across various medical specialties
            </p>
          </div>

          <div style={styles.grid}>
            {specializationsWithMetadata.map((spec) => {
              return (
                <div
                  key={spec.id}
                  style={styles.card}
                  className="hover-lift"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(40, 108, 255, 0.15)';
                    e.currentTarget.style.borderColor = '#286CFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8ECF0';
                  }}
                >
                  <div style={styles.cardTop}>
                    <div style={styles.svgWrapper}>
                      {getSpecialtySVG(spec.name)}
                    </div>
                    <div style={styles.badge}>{spec.availability}</div>
                  </div>

                  <h3 style={styles.cardTitle}>{spec.name}</h3>
                  <p style={styles.cardDescription}>{spec.description}</p>

                  <div style={styles.responseTimeWrapper}>
                    <Clock size={16} style={{ color: '#28C08A' }} />
                    <span>{spec.responseTime} response</span>
                  </div>

                  <button 
                    style={styles.consultBtn}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#133E97';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#286CFF';
                    }}
                  >
                    Consult Now <ArrowRight size={18} style={{ marginLeft: '6px' }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section style={styles.whySection}>
        <div style={styles.container}>
          <h2 style={styles.whyTitle}>Why Choose GEWO Health?</h2>
          <div style={styles.whyGrid}>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>
                <CheckCircle size={32} style={{ color: '#286CFF' }} />
              </div>
              <h3 style={styles.whyCardTitle}>Verified Doctors</h3>
              <p style={styles.whyCardText}>All our doctors are verified and licensed medical professionals</p>
            </div>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>
                <Clock size={32} style={{ color: '#28C08A' }} />
              </div>
              <h3 style={styles.whyCardTitle}>Quick Response</h3>
              <p style={styles.whyCardText}>Get medical advice within 5 minutes of consultation request</p>
            </div>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>
                <Activity size={32} style={{ color: '#FFD28A' }} />
              </div>
              <h3 style={styles.whyCardTitle}>24/7 Available</h3>
              <p style={styles.whyCardText}>Round-the-clock availability for all medical emergencies</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  banner: {
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 50%, #133E97 100%)',
    padding: '120px 0 80px',
    marginTop: '70px'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  bannerContent: {
    textAlign: 'center',
    color: 'white'
  },
  bannerTitle: {
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '16px'
  },
  bannerSubtitle: {
    fontSize: '18px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto 32px',
    opacity: 0.95
  },
  features: {
    display: 'flex',
    justifyContent: 'center',
    gap: '32px',
    flexWrap: 'wrap'
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '500'
  },
  section: {
    padding: '80px 0',
    background: '#F4F7FB'
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '48px'
  },
  sectionTitle: {
    fontSize: '40px',
    fontWeight: '700',
    lineHeight: '1.2',
    color: '#4A4A4A',
    marginBottom: '16px'
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#7C8A9A',
    maxWidth: '600px',
    margin: '0 auto'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '24px'
  },
  card: {
    background: 'white',
    border: '1px solid #E8ECF0',
    borderRadius: '16px',
    padding: '24px',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  svgWrapper: {
    width: '120px',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badge: {
    background: '#FFD28A',
    color: '#4A4A4A',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#4A4A4A',
    marginBottom: '8px'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#7C8A9A',
    lineHeight: '1.6',
    marginBottom: '20px',
    minHeight: '42px'
  },
  responseTimeWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#7C8A9A',
    marginBottom: '20px',
    padding: '12px 16px',
    background: '#F4F7FB',
    borderRadius: '8px',
    fontWeight: '500'
  },
  consultBtn: {
    width: '100%',
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '26px',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  whySection: {
    padding: '80px 0',
    background: 'white'
  },
  whyTitle: {
    fontSize: '40px',
    fontWeight: '700',
    textAlign: 'center',
    color: '#4A4A4A',
    marginBottom: '48px'
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '32px'
  },
  whyCard: {
    textAlign: 'center',
    padding: '32px'
  },
  whyIcon: {
    width: '80px',
    height: '80px',
    background: '#F4F7FB',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  whyCardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#4A4A4A',
    marginBottom: '12px'
  },
  whyCardText: {
    fontSize: '15px',
    color: '#7C8A9A',
    lineHeight: '1.6'
  }
};

export default AllSpecializations;
